import { Tags } from '@/components/organisms/CASDataGrid/grid-columns/columns/student/Tags';
import { ApplicantRow, CellProps } from '@/components/organisms/CASDataGrid/types';
import AlertDialog from '@/components/pages/modals/AlertDialog';
import { studentsUnpinTag } from '@/store/apply/actions';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { getAllTasksFromRequirements } from '@/utilities/transformRequirements';
import { Badge, Box, Button, Flex, Text } from '@chakra-ui/react';
import { faAngleRight, faFilePlus, faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OfferTooltipLabels } from 'data';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, To } from 'react-router-dom';
import { getStatusByConditionType, RoundBadgeWithTooltip, uiTypes } from 'shared-components';
import { IApplicantOffer } from 'types';
import { getApplicationOfferBadgeProps } from '../../../utilities/getApplicationOfferBadgeProps';
import OfferStatusBadge from '../offerStatusBadge';

interface ProgrammeCardProps {
  offer: IApplicantOffer;
  userId: string;
  to: To;
}

export const ProgrammeCard = ({ offer, userId, to }: ProgrammeCardProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const badgeProps = getApplicationOfferBadgeProps(offer.status);
  const allTasks = getAllTasksFromRequirements(offer.requirements);
  const hasRequirementsBeenEdited = allTasks.some(
    task => Object.keys(task?.pendingChanges ?? {}).length > 0 && !task.lastUpdatedByApplicant,
  );
  const hasBeenEdited = Object.keys(offer?.pendingChanges ?? {}).length > 0 || hasRequirementsBeenEdited;
  const hasBeenEditedByApplicant = allTasks.some(
    task => Object.keys(task?.pendingChanges ?? {}).length > 0 && task.lastUpdatedByApplicant,
  );
  const [tagDeleteModalData, setTagDeleteModalData] = useState({
    clickAction: () => {},
    title: '',
    buttonName: '',
  });
  const [isTagDeleteConfirmationModal, setIsTagDeleteConfirmationModal] = useState(false);

  const onTagDelete = useCallback((tagId: string, _, row: ApplicantRow) => {
    setTagDeleteModalData({
      title: 'Remove Tag From Offer?',
      buttonName: 'Confirm',
      clickAction: () => {
        const tag = { id: tagId };
        dispatch(studentsUnpinTag(row?.offerId, userId, tag));
        setIsTagDeleteConfirmationModal(false);
      },
    });
    setIsTagDeleteConfirmationModal(true);
  }, []);
  const applicantStatus = getStatusByConditionType(offer?.applicantStatus, offer?.conditionType);
  // Note: Tags component expects CellProps typed for Row, but we're passing ApplicantRow data.
  // This works at runtime but requires a type assertion due to the type mismatch.
  const tagsProps = {
    row: { ...offer, offerId: offer.id, onTagDelete } as unknown as ApplicantRow,
    column: {
      key: 'tags',
      name: 'Tags',
    } as CellProps['column'],
    rowIdx: 0,
    isCellSelected: false,
    isRowSelected: false,
    onRowSelectionChange: () => {},
  } as unknown as CellProps;

  return (
    <Button
      as={RouterLink}
      to={to}
      variant="outline"
      width="100%"
      height="auto"
      borderColor="chakra-border-color"
      padding={4}
      display="block"
      textAlign="left"
    >
      <AlertDialog
        isOpen={isTagDeleteConfirmationModal}
        modalTitle="Remove tag"
        descriptionText={`Are you sure you want to remove this tag?`}
        confirmationButtonText={`Remove`}
        id={'remove-tag'}
        onClickSuccess={() => tagDeleteModalData.clickAction()}
        onClose={() => setIsTagDeleteConfirmationModal(false)}
      />
      <Flex width="100%" alignItems="center" justifyContent="space-between">
        <Box maxW={'60%'}>
          <Text fontSize="lg" isTruncated wordBreak="break-word" overflowWrap="break-word">
            {offer.programme?.courseName}
          </Text>
          <Text fontSize="md" color="chakra-subtle-text" isTruncated wordBreak="break-word" overflowWrap="break-word">
            {`${offer.programme?.mode || ''}${offer.programme?.campus ? `, ${offer.programme.campus}` : ''}, ${
              offer.intakeId ? formatYearMonthDate(offer.intakeId) : ''
            }, ${offer.programme?.entryPoint || ''}`}
            {' entry'}
          </Text>
        </Box>

        <Flex alignItems="center" gap={2}>
          <OfferStatusBadge applicantStatus={applicantStatus} iconSize="sm" />
          {hasBeenEdited && (
            <RoundBadgeWithTooltip
              icon={faPen}
              label={OfferTooltipLabels.pendingChanges}
              colorScheme="orange"
              size="sm"
            />
          )}
          {hasBeenEditedByApplicant && (
            <RoundBadgeWithTooltip
              icon={faFilePlus}
              label={OfferTooltipLabels.pendingChangesByApplicant}
              colorScheme="indigo"
              size="sm"
            />
          )}
          <Badge size="md" colorScheme={badgeProps.colorScheme}>
            {badgeProps.children}
          </Badge>
          <Box>
            <FontAwesomeIcon icon={faAngleRight} color="gray" />
          </Box>
        </Flex>
      </Flex>
      <Tags cellProps={tagsProps} role={uiTypes.UserRole.Admin} />
    </Button>
  );
};
